/**
 * Commission Dashboard — F009
 *
 * Analytics dashboard with:
 * - 4 KPI summary cards (Unmatched Transactions, This Month Commission,
 *   Pending RCTI Generation, Awaiting Payment)
 * - Revenue by Month stacked bar chart (upfront vs trail)
 * - Revenue by Lender breakdown table (top 10)
 * - Revenue by Partner breakdown table (top 10)
 * - Recent Transactions table (last 10)
 *
 * @sprint SPRINT-20
 * @feature F009
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertCircle,
  DollarSign,
  FileText,
  Clock,
} from "lucide-react";
import { format } from "date-fns";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DashboardSummary {
  unmatchedTransactions: number;
  thisMonthCommission: string;
  pendingRctiGeneration: number;
  awaitingPayment: string;
}

interface RevenueByMonth {
  month: string;
  upfront: string;
  trail: string;
  total: string;
}

interface RevenueByLender {
  lenderName: string;
  totalCommission: string;
  transactionCount: number;
}

interface RevenueByPartner {
  partnerName: string;
  totalPaid: string;
  transactionCount: number;
}

interface CommissionTransaction {
  id: string;
  transactionDate: string;
  lenderName: string;
  lenderAccountNumber: string;
  commissionType: string;
  commissionAmount: string;
  totalAmount: string;
  processingStatus: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (value: string | null | undefined): string => {
  if (!value) return "$0";
  const num = parseFloat(value);
  if (isNaN(num)) return "$0";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const formatDate = (value: string | null): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

const PROCESSING_STATUS_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  pending: {
    label: "Pending",
    className: "bg-slate-100 text-slate-700 border-slate-300",
  },
  imported: {
    label: "Imported",
    className: "bg-blue-100 text-blue-700 border-blue-300",
  },
  matched: {
    label: "Matched",
    className: "bg-green-100 text-green-700 border-green-300",
  },
  calculated: {
    label: "Calculated",
    className: "bg-yellow-100 text-yellow-700 border-yellow-300",
  },
  paid: {
    label: "Paid",
    className: "bg-emerald-100 text-emerald-700 border-emerald-300",
  },
  disputed: {
    label: "Disputed",
    className: "bg-red-100 text-red-700 border-red-300",
  },
  void: {
    label: "Void",
    className: "bg-slate-100 text-slate-500 border-slate-200",
  },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface SummaryCardProps {
  testId: string;
  valueTestId: string;
  title: string;
  value: string | number;
  icon: React.ReactNode;
  isLoading: boolean;
  href?: string;
}

function SummaryCard({
  testId,
  valueTestId,
  title,
  value,
  icon,
  isLoading,
  href,
}: SummaryCardProps) {
  const content = (
    <Card
      data-testid={testId}
      className={href ? "cursor-pointer hover:shadow-md transition-shadow" : ""}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <div
            data-testid={valueTestId}
            className="text-2xl font-bold"
          >
            {value}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (href) {
    return <Link href={href}>{content}</Link>;
  }
  return content;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function CommissionDashboard() {
  const { data: summary, isLoading: summaryLoading } =
    useQuery<DashboardSummary>({
      queryKey: ["/api/commissions/dashboard/summary"],
    });

  const { data: revenueByMonth = [], isLoading: monthLoading } = useQuery<
    RevenueByMonth[]
  >({
    queryKey: ["/api/commissions/dashboard/revenue-by-month"],
  });

  const { data: revenueByLender = [], isLoading: lenderLoading } = useQuery<
    RevenueByLender[]
  >({
    queryKey: ["/api/commissions/dashboard/revenue-by-lender"],
  });

  const { data: revenueByPartner = [], isLoading: partnerLoading } = useQuery<
    RevenueByPartner[]
  >({
    queryKey: ["/api/commissions/dashboard/revenue-by-partner"],
  });

  const { data: recentTransactions = [], isLoading: txLoading } = useQuery<
    CommissionTransaction[]
  >({
    queryKey: ["/api/commissions/transactions", { limit: "10" }],
    queryFn: async () => {
      const response = await fetch(
        "/api/commissions/transactions?limit=10"
      );
      if (!response.ok) throw new Error("Failed to fetch recent transactions");
      return response.json();
    },
  });

  // Format chart data (convert string numerics to numbers for Recharts)
  const chartData = revenueByMonth.map((row) => ({
    month: row.month,
    Upfront: parseFloat(row.upfront),
    Trail: parseFloat(row.trail),
  }));

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------------------------ */}
      {/* KPI Summary Cards                                                   */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          testId="card-unmatched-transactions"
          valueTestId="value-unmatched-transactions"
          title="Unmatched Transactions"
          value={summary?.unmatchedTransactions ?? 0}
          icon={<AlertCircle className="h-4 w-4" />}
          isLoading={summaryLoading}
          href="/commission/matching"
        />
        <SummaryCard
          testId="card-this-month-commission"
          valueTestId="value-this-month-commission"
          title="This Month Commission"
          value={formatCurrency(summary?.thisMonthCommission)}
          icon={<DollarSign className="h-4 w-4" />}
          isLoading={summaryLoading}
        />
        <SummaryCard
          testId="card-pending-rcti"
          valueTestId="value-pending-rcti"
          title="Pending RCTI Generation"
          value={summary?.pendingRctiGeneration ?? 0}
          icon={<FileText className="h-4 w-4" />}
          isLoading={summaryLoading}
          href="/commission/rcti"
        />
        <SummaryCard
          testId="card-awaiting-payment"
          valueTestId="value-awaiting-payment"
          title="Awaiting Payment"
          value={formatCurrency(summary?.awaitingPayment)}
          icon={<Clock className="h-4 w-4" />}
          isLoading={summaryLoading}
        />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Revenue by Month Chart                                              */}
      {/* ------------------------------------------------------------------ */}
      <Card data-testid="chart-revenue-by-month">
        <CardHeader>
          <CardTitle>Revenue by Month</CardTitle>
        </CardHeader>
        <CardContent>
          {monthLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : chartData.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-center text-muted-foreground space-y-2">
              <p className="text-sm font-medium">No data yet</p>
              <p className="text-xs">Import your first commission statement</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={chartData}
                margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v: number) =>
                    `$${(v / 1000).toFixed(0)}k`
                  }
                />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    formatCurrency(String(value)),
                    name,
                  ]}
                />
                <Legend />
                <Bar dataKey="Upfront" stackId="a" fill="#3b82f6" />
                <Bar dataKey="Trail" stackId="a" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Breakdown tables (side by side on wide screens)                     */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Revenue by Lender */}
        <Card>
          <CardHeader>
            <CardTitle>Revenue by Lender</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {lenderLoading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              <Table data-testid="table-revenue-by-lender">
                <TableHeader>
                  <TableRow>
                    <TableHead>Lender</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead className="text-right">Transactions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {revenueByLender.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="py-6 text-center text-sm text-muted-foreground"
                      >
                        No lender data yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    revenueByLender.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">
                          {row.lenderName}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(row.totalCommission)}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.transactionCount}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Revenue by Partner */}
        <Card>
          <CardHeader>
            <CardTitle>Revenue by Partner</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {partnerLoading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              <Table data-testid="table-revenue-by-partner">
                <TableHeader>
                  <TableRow>
                    <TableHead>Partner</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Transactions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {revenueByPartner.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="py-6 text-center text-sm text-muted-foreground"
                      >
                        No partner data yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    revenueByPartner.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">
                          {row.partnerName}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(row.totalPaid)}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.transactionCount}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Recent Transactions                                                 */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {txLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : (
            <Table data-testid="table-recent-transactions">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Lender</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-6 text-center text-sm text-muted-foreground"
                    >
                      No transactions yet. Import your first commission statement.
                    </TableCell>
                  </TableRow>
                ) : (
                  recentTransactions.map((tx) => {
                    const statusCfg =
                      PROCESSING_STATUS_CONFIG[tx.processingStatus] ?? {
                        label: tx.processingStatus,
                        className:
                          "bg-slate-100 text-slate-700 border-slate-300",
                      };
                    return (
                      <TableRow key={tx.id}>
                        <TableCell className="whitespace-nowrap text-sm">
                          {formatDate(tx.transactionDate)}
                        </TableCell>
                        <TableCell className="max-w-[140px] truncate text-sm">
                          {tx.lenderName}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {tx.lenderAccountNumber}
                        </TableCell>
                        <TableCell className="capitalize text-sm">
                          {tx.commissionType}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {formatCurrency(tx.commissionAmount)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`text-xs ${statusCfg.className}`}
                            data-testid={`status-badge-${tx.processingStatus}`}
                          >
                            {statusCfg.label}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
